import { useCallback, useEffect, useState } from 'react';
import { progressRepository } from '@/storage/repository';
import { recommend } from '@/training/recommendations';
import type { ChordStats, Recommendation, SessionSummary, TransitionStats } from '@/types/training';

export interface ProgressData {
  sessions: SessionSummary[];
  transitions: TransitionStats[];
  chords: ChordStats[];
  recommendation: Recommendation;
  /**
   * When the data was read, in epoch ms. Views that filter by a time window
   * anchor on this rather than on `Date.now()` during render — a clock read in
   * render is impure and would make the window drift on every re-render.
   */
  loadedAt: number;
  loading: boolean;
}

/**
 * The starter recommendation, shown before anything has loaded.
 *
 * Built once at module scope: with no history the engine returns the fixed
 * `firstSession` suggestion, which does not depend on the current time.
 */
const INITIAL_RECOMMENDATION = recommend({ transitions: [], chords: [], sessions: [], now: 0 });

const EMPTY: ProgressData = {
  sessions: [],
  transitions: [],
  chords: [],
  recommendation: INITIAL_RECOMMENDATION,
  loadedAt: 0,
  loading: true,
};

/**
 * Loads everything the home and progress screens need, and derives the next
 * recommendation from it.
 */
export function useProgress(): ProgressData & { reload: () => void } {
  const [state, setState] = useState<ProgressData>(EMPTY);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [sessions, transitions, chords] = await Promise.all([
          progressRepository.recentSessions(60),
          progressRepository.allTransitions(),
          progressRepository.allChordStats(),
        ]);
        if (cancelled) return;
        const now = Date.now();
        setState({
          sessions,
          transitions,
          chords,
          recommendation: recommend({ sessions, transitions, chords, now }),
          loadedAt: now,
          loading: false,
        });
      } catch (error) {
        // A storage failure must not blank the screen: keep the starter
        // recommendation and let the player practise anyway.
        console.error('[storage] failed to load progress', error);
        if (!cancelled) setState((previous) => ({ ...previous, loading: false, loadedAt: Date.now() }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [version]);

  const reload = useCallback(() => setVersion((value) => value + 1), []);

  return { ...state, reload };
}
